import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { animate, motion, useMotionValue, useMotionValueEvent, useTransform } from "framer-motion";
import { FiCheck, FiChevronDown, FiChevronLeft, FiFileText, FiImage, FiLock, FiMapPin, FiNavigation, FiPrinter, FiUser } from "react-icons/fi";
import PhoneFrame from "./PhoneFrame";
import { ORDER_NO, PrinterUnit, ReadyStamp, Sheet, ShopSign } from "./Printer";
import { SHOP } from "./demoData";
import { usePrefersReducedMotion } from "./motion";

/*
 * The hero: one continuous story of an order, driven by a single progress value (0 -> 1, 18 s loop).
 *
 *   0.00-0.17  pick the file on the phone, upload
 *   0.17-0.33  copies, colour, sides; the price updates
 *   0.33-0.50  pay by UPI, success tick; the job flies to the printer
 *   0.50-0.87  the shop's printer pushes three sheets out of its front slot onto the tray; the phone tracks it live
 *   0.87-0.94  ready to collect
 *   0.94-1.00  collected: the pile leaves with the student
 *
 * Runs only while on screen. With reduced motion the finished order is shown instead.
 */

const LOOP = 18;
const SHEETS = 3;
const PHONE_W = 240; // the phone is laid out at this width and scaled to the stage

const W = {
  upload: [0, 0.17],
  settings: [0.17, 0.33],
  pay: [0.33, 0.5],
  flight: [0.46, 0.53],
  feed: (i) => [0.53 + i * 0.11, 0.6 + i * 0.11],
  stamp: [0.87, 0.9],
  collect: [0.94, 0.985],
};

const PHASES = [
  [0, "Uploading assignment-3.pdf, 12 pages"],
  [0.17, "1 copy, both sides, colour on 2 pages: ₹22"],
  [0.33, "Paying ₹22 by UPI"],
  [0.44, `Paid. Order ${ORDER_NO} is on its way to the shop`],
  [0.53, `Printing at ${SHOP.name}, 3 sheets`],
  [0.87, `Ready. Collect with order ${ORDER_NO}`],
  [0.94, "Collected. That's the whole thing."],
];
const phaseFor = (v) => {
  let idx = 0;
  for (let i = 0; i < PHASES.length; i += 1) if (v >= PHASES[i][0]) idx = i;
  return idx;
};
const labelFor = (v) => {
  if (v >= 0.87) return `Order ${ORDER_NO} done`;
  if (v >= 0.53) return `Printing ${Math.min(SHEETS, Math.floor((v - 0.53) / 0.11) + 1)} of ${SHEETS}`;
  if (v >= 0.46) return `Receiving order ${ORDER_NO}`;
  return "Ready";
};

/** Visible from s to e, cross-fading with its neighbours. */
function useWindow(p, s, e, f = 0.012) {
  const inFrom = s <= 0 ? -0.001 : s - f;
  const outTo = e >= 1 ? 1.001 : e + f;
  return useTransform(p, [inFrom, s, e, outTo], [s <= 0 ? 1 : 0, 1, 1, e >= 1 ? 1 : 0], { clamp: true });
}
function useSeg(p, a, b, from = 0, to = 1) {
  return useTransform(p, [a, b], [from, to], { clamp: true });
}

export default function StoryScene({ className = "" }) {
  const reduced = usePrefersReducedMotion();
  const p = useMotionValue(reduced ? 0.9 : 0);
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const phoneRef = useRef(null);
  const slotRef = useRef(null);
  const pathRef = useRef(null);
  const controlsRef = useRef(null);
  const [phase, setPhase] = useState(reduced ? 5 : 0);
  const [label, setLabel] = useState(reduced ? labelFor(0.9) : "Ready");
  const [inView, setInView] = useState(false);
  const [geom, setGeom] = useState({ path: "", len: 0 });

  useMotionValueEvent(p, "change", (v) => {
    const next = phaseFor(v);
    setPhase((cur) => (cur === next ? cur : next));
    const l = labelFor(v);
    setLabel((cur) => (cur === l ? cur : l));
  });

  // the loop: created once, paused while off screen or in a background tab
  useEffect(() => {
    if (reduced) {
      p.set(0.9);
      return undefined;
    }
    const controls = animate(p, [0, 1], { duration: LOOP, ease: "linear", repeat: Infinity });
    controls.pause();
    controlsRef.current = controls;
    return () => {
      controls.stop();
      controlsRef.current = null;
    };
  }, [reduced, p]);

  useEffect(() => {
    if (reduced) return undefined;
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    const onVisibility = () => {
      const c = controlsRef.current;
      if (!c) return;
      if (document.hidden) c.pause();
      else if (inView) c.play();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [reduced, inView]);

  useEffect(() => {
    const c = controlsRef.current;
    if (!c) return;
    if (inView && !document.hidden) c.play();
    else c.pause();
  }, [inView]);

  // geometry: where sheets land, the wire from the phone to the printer, the phone's scale
  useLayoutEffect(() => {
    const measure = () => {
      const stage = stageRef.current;
      const phone = phoneRef.current;
      const slot = slotRef.current;
      if (!stage || !phone || !slot) return;
      const s = stage.getBoundingClientRect();
      const small = typeof window.matchMedia === "function" && window.matchMedia("(max-width: 640px)").matches;
      // the phone takes at most a share of the width and 80% of the height; the printer must leave room for a sheet above it
      const phoneScale = Math.min((s.width * (small ? 0.4 : 0.34)) / PHONE_W, (s.height * 0.8) / (PHONE_W * (19.2 / 9)));
      const printerW = Math.min(s.width * (small ? 0.54 : 0.48), s.height * 0.66);
      stage.style.setProperty("--phone-scale", String(phoneScale));
      stage.style.setProperty("--printer-w", `${Math.round(printerW)}px`);
      const ph = phone.getBoundingClientRect();
      const sl = slot.getBoundingClientRect();
      const x0 = ph.right - s.left - 6;
      const y0 = ph.top - s.top + ph.height * 0.5;
      const x1 = sl.left - s.left + sl.width / 2;
      const y1 = sl.top - s.top + 2;
      const path = `M ${x0.toFixed(1)} ${y0.toFixed(1)} C ${(x0 + (x1 - x0) * 0.55).toFixed(1)} ${y0.toFixed(1)}, ${x1.toFixed(1)} ${(y1 - s.height * 0.22).toFixed(1)}, ${x1.toFixed(1)} ${y1.toFixed(1)}`;
      setGeom((g) => (g.path === path ? g : { ...g, path }));
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && stageRef.current) ro.observe(stageRef.current);
    window.addEventListener("resize", measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    const el = pathRef.current;
    if (!el || !geom.path) return;
    const len = el.getTotalLength();
    setGeom((g) => (g.len === len ? g : { ...g, len }));
  }, [geom.path]);

  const stageOpacity = useTransform(p, [0, 0.02, 0.985, 1], [0, 1, 1, 0], { clamp: true });
  const wireDraw = useSeg(p, W.flight[0], W.flight[0] + 0.05);
  const wireOpacity = useTransform(p, [0.46, 0.47, 0.56, 0.6], [0, 0.7, 0.7, 0], { clamp: true });
  const stampOpacity = useTransform(p, [0.87, 0.9, 0.94, 0.96], [0, 1, 1, 0], { clamp: true });
  const stampY = useTransform(p, [0.87, 0.9], [12, 0], { clamp: true });
  const stampScale = useTransform(p, [0.87, 0.9], [0.92, 1], { clamp: true });
  const state = phase >= 5 ? "done" : phase >= 3 ? "busy" : "idle";

  return (
    <div className={`story is-${state} ${className}`} ref={rootRef} aria-hidden="true">
      <motion.div className="story__stage" ref={stageRef} style={{ opacity: stageOpacity }}>
        <div className="press__bg" />
        <div className="press__counter" />
        <ShopSign />
        <StoryPhone p={p} phoneRef={phoneRef} />
        <svg className="story__wire" width="100%" height="100%">
          <motion.path ref={pathRef} d={geom.path || "M 0 0"} fill="none" stroke="#f25c05" strokeWidth="2" strokeLinecap="round" style={{ pathLength: wireDraw, opacity: wireOpacity }} />
          {[0, 0.025, 0.05].map((d) => (
            <Packet key={d} p={p} pathRef={pathRef} len={geom.len} delay={d} />
          ))}
        </svg>
        <div className="story__printer">
          <PrinterUnit label={label}>
            <span className="story__shelf" />
            <span className="story__glow" />
            {Array.from({ length: SHEETS }, (_, i) => (
              <StorySheet key={i} p={p} i={i} slotRef={i === 0 ? slotRef : undefined} />
            ))}
          </PrinterUnit>
        </div>
        <motion.div className="story__stamp" style={{ opacity: stampOpacity, y: stampY, scale: stampScale }}>
          <ReadyStamp />
        </motion.div>
      </motion.div>
      <div className="story__status" key={phase}>
        <span className={`story__dot ${phase >= 5 ? "is-done" : ""}`} />
        {PHASES[phase][1]}
      </div>
    </div>
  );
}

/** One data packet flying along the wire. */
function Packet({ p, pathRef, len, delay }) {
  const t = useTransform(p, [W.flight[0] + delay, W.flight[1] + delay], [0, 1], { clamp: true });
  const point = useTransform(t, (v) => {
    const el = pathRef.current;
    if (!el || !len) return { x: -100, y: -100 };
    return el.getPointAtLength(v * len);
  });
  const cx = useTransform(point, (q) => q.x);
  const cy = useTransform(point, (q) => q.y);
  const opacity = useTransform(t, [0, 0.06, 0.94, 1], [0, 1, 1, 0], { clamp: true });
  return <motion.circle r="5" fill="#f25c05" style={{ cx, cy, opacity }} />;
}

/** One sheet: slides out of the front slot onto the tray (on top of the previous ones), lifts away when collected. */
function StorySheet({ p, i, slotRef }) {
  const [f0, f1] = W.feed(i);
  const [c0, c1] = W.collect;
  const feedY = useTransform(p, [f0, f1], ["-103%", "0%"], { clamp: true });
  const x = useTransform(p, [c0, c1], [i * 3, i * 3 - 60], { clamp: true });
  const y = useTransform(p, [c0, c1], [i * 5, i * 5 + 90], { clamp: true });
  const rotate = useTransform(p, [c0, c1], [(i - 1) * 1.2, -12], { clamp: true });
  const opacity = useTransform(p, [c0, c1], [1, 0], { clamp: true });
  return (
    <motion.div className="story__out" ref={slotRef} style={{ x, y, rotate, opacity, zIndex: 3 + i }}>
      <motion.div className="story__feed" style={{ y: feedY }}>
        <div className="story__flat">
          <Sheet kind={i} />
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------------- the phone */

function StoryPhone({ p, phoneRef }) {
  const filesIn = useWindow(p, W.upload[0], W.upload[1]);
  const settingsIn = useWindow(p, W.settings[0], W.settings[1]);
  const payIn = useWindow(p, W.pay[0], W.pay[1]);
  const trackIn = useWindow(p, W.pay[1], 1);

  // upload
  const pick = useSeg(p, 0.04, 0.07);
  const pickBorder = useTransform(pick, [0, 1], ["#e8e4dc", "#f25c05"]);
  const pickBg = useTransform(pick, [0, 1], ["#ffffff", "#fff6ef"]);
  const barIn = useSeg(p, 0.07, 0.08);
  const upload = useSeg(p, 0.07, 0.15, 0, 100);
  const uploadWidth = useTransform(upload, (v) => `${v}%`);
  const uploadLabel = useTransform(upload, (v) => `${Math.round(v)}%`);
  const pctIn = useTransform(p, [0.07, 0.08, 0.15, 0.16], [0, 1, 1, 0], { clamp: true });
  // settings
  const previewColour = useSeg(p, 0.235, 0.26);
  const previewFilter = useTransform(previewColour, (v) => `grayscale(${1 - v})`);
  const colourX = useSeg(p, 0.23, 0.26, 0, 1);
  const colourLeft = useTransform(colourX, (v) => `${v * 50}%`);
  const sidesX = useSeg(p, 0.27, 0.3, 0, 1);
  const sidesLeft = useTransform(sidesX, (v) => `${v * 50}%`);
  const price = useTransform(p, (v) => (v > 0.25 ? "₹22" : "₹14"));
  // pay
  const pressed = useTransform(p, [0.39, 0.41, 0.43], [1, 0.95, 1], { clamp: true });
  const payBtnOut = useTransform(p, [0.41, 0.44], [1, 0], { clamp: true });
  const successIn = useSeg(p, 0.41, 0.43);
  const tick = useSeg(p, 0.42, 0.47);
  const tickScale = useTransform(p, [0.41, 0.45], [0.6, 1], { clamp: true });
  const payLabelIn = useSeg(p, 0.44, 0.48);
  // tracking
  const queue = useTransform(p, [0.52, 0.86], [0, 100], { clamp: true });
  const queueWidth = useTransform(queue, (v) => `${v}%`);
  const trackTitle = useTransform(p, (v) => (v > 0.94 ? "Collected" : v > 0.87 ? "Ready to collect" : v > 0.53 ? "Printing" : "In queue"));
  const trackSub = useTransform(p, (v) => (v > 0.94 ? "Thanks for printing with us" : v > 0.87 ? `Show order ${ORDER_NO} at the counter` : v > 0.53 ? "3 sheets, about 2 minutes" : "Sent to the shop's printer"));
  const step2 = useSeg(p, 0.53, 0.55);
  const step3 = useSeg(p, 0.87, 0.89);
  const step4 = useSeg(p, 0.94, 0.96);
  const step2Op = useTransform(step2, [0, 1], [0.45, 1]);
  const step2Bg = useTransform(step2, [0, 1], ["#e2ddd5", "#1e9e5a"]);
  const step3Op = useTransform(step3, [0, 1], [0.45, 1]);
  const step3Bg = useTransform(step3, [0, 1], ["#e2ddd5", "#1e9e5a"]);
  const step4Op = useTransform(step4, [0, 1], [0.45, 1]);
  const step4Bg = useTransform(step4, [0, 1], ["#e2ddd5", "#f25c05"]);

  return (
    <div className="story__phone" ref={phoneRef}>
      <PhoneFrame>
        {/* files */}
        <motion.div className="app" style={{ opacity: filesIn }}>
          <div className="app__hdr">
            <div className="app__hdr-row">
              <span className="app__brand">
                <img src="/img/print_anywhere_logo2.webp" alt="" />
                <b>Print</b>Anywhere
              </span>
              <i className="app__avatar">
                <FiUser />
              </i>
            </div>
            <small>Collect at</small>
            <strong>{SHOP.name}</strong>
            <span>
              <FiMapPin /> {SHOP.address}, {SHOP.distance}, open
            </span>
          </div>
          <div className="app__body">
            <div className="app__label-row">
              <small className="app__label">Your files</small>
              <em>Select all</em>
            </div>
            <motion.div className="pfile" style={{ borderColor: pickBorder, backgroundColor: pickBg }}>
              <span className="pfile__icon pfile__icon--pdf">
                <FiFileText />
              </span>
              <span className="pfile__body">
                <strong>assignment-3.pdf</strong>
                <small>12 pages, 1.2 MB</small>
                <motion.span className="pfile__bar" style={{ opacity: barIn }}>
                  <motion.i style={{ width: uploadWidth }} />
                </motion.span>
              </span>
              <motion.span className="pfile__check" style={{ scale: pick, opacity: pick }}>
                <FiCheck />
              </motion.span>
              <motion.span className="pfile__pct" style={{ opacity: pctIn }}>
                {uploadLabel}
              </motion.span>
            </motion.div>
            <div className="pfile">
              <span className="pfile__icon pfile__icon--img">
                <FiImage />
              </span>
              <span className="pfile__body">
                <strong>passport-photo.jpg</strong>
                <small>1 page, 640 KB</small>
              </span>
            </div>
            <div className="pfile">
              <span className="pfile__icon pfile__icon--pdf">
                <FiFileText />
              </span>
              <span className="pfile__body">
                <strong>lab-report.pdf</strong>
                <small>6 pages, 1.1 MB</small>
              </span>
            </div>
            <div className="pscreen__cta">
              <FiPrinter /> Print 1 file
            </div>
          </div>
        </motion.div>

        {/* settings */}
        <motion.div className="app" style={{ opacity: settingsIn }}>
          <div className="app__hdr app__hdr--bar">
            <FiChevronLeft />
            <strong>assignment-3.pdf</strong>
            <em>Apply to all</em>
          </div>
          <div className="app__body">
            <div className="pscreen__preview">
              <motion.div className="pscreen__page" style={{ filter: previewFilter }}>
                <i />
                <i />
                <i />
                <b />
              </motion.div>
              <div className="pscreen__page pscreen__page--grey">
                <i />
                <i />
                <i />
                <i />
              </div>
            </div>
            <div className="prow">
              <span>Copies</span>
              <span className="pstep">
                <i>−</i>
                <b>1</b>
                <i>+</i>
              </span>
            </div>
            <div className="prow prow--col">
              <span>Side</span>
              <span className="pseg pseg--two">
                <motion.i style={{ left: sidesLeft }} />
                <em>Single</em>
                <em>Both sides</em>
              </span>
            </div>
            <div className="prow prow--col">
              <span>Colour</span>
              <span className="pseg pseg--four">
                <motion.i style={{ left: colourLeft }} />
                <em>B&amp;W</em>
                <em>Colour</em>
                <em>Both</em>
                <em>Custom</em>
              </span>
            </div>
            <div className="prow">
              <span>Page size</span>
              <b>
                A4 <FiChevronDown />
              </b>
            </div>
            <div className="pscreen__cta pscreen__cta--split">
              <span>
                Total <motion.b>{price}</motion.b>
              </span>
              <span>Done</span>
            </div>
          </div>
        </motion.div>

        {/* pay */}
        <motion.div className="app" style={{ opacity: payIn }}>
          <div className="app__hdr app__hdr--bar">
            <FiChevronLeft />
            <strong>Review your order and pay</strong>
          </div>
          <div className="app__body">
            <div className="pcard">
              <small>Collect at</small>
              <strong>{SHOP.name}</strong>
              <span>
                {SHOP.address}, {SHOP.distance}
              </span>
            </div>
            <div className="pcard pcard--bill">
              <small>Bill details</small>
              <div>
                <span>Black and white, 10 pages</span>
                <b>₹10</b>
              </div>
              <div>
                <span>Colour, 2 pages</span>
                <b>₹10</b>
              </div>
              <div>
                <span>Convenience fee</span>
                <b>₹2</b>
              </div>
              <div className="is-total">
                <span>Total</span>
                <b>₹22</b>
              </div>
            </div>
            <div className="pupi">
              <small>Pay with</small>
              <span className="pupi__apps">
                <i style={{ background: "#5f259f" }}>Pe</i>
                <i style={{ background: "#1a73e8" }}>G</i>
                <i style={{ background: "#00baf2" }}>P</i>
                <i style={{ background: "#15120f" }}>UPI</i>
              </span>
            </div>
            <motion.div className="pscreen__cta pscreen__cta--pay" style={{ scale: pressed, opacity: payBtnOut }}>
              <FiLock /> Pay ₹22
            </motion.div>
            <motion.div className="psuccess" style={{ opacity: successIn, scale: tickScale }}>
              <svg viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="29" fill="#e6f6ec" />
                <motion.circle cx="32" cy="32" r="29" fill="none" stroke="#1e9e5a" strokeWidth="4" style={{ pathLength: tick }} strokeLinecap="round" transform="rotate(-90 32 32)" />
                <motion.path d="M20 33 l8 8 l17 -18" fill="none" stroke="#1e9e5a" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" style={{ pathLength: tick }} />
              </svg>
              <motion.strong style={{ opacity: payLabelIn }}>Payment successful</motion.strong>
              <motion.small style={{ opacity: payLabelIn }}>Order {ORDER_NO}, sent to the printer</motion.small>
            </motion.div>
          </div>
        </motion.div>

        {/* tracking */}
        <motion.div className="app" style={{ opacity: trackIn }}>
          <div className="app__hdr app__hdr--bar">
            <FiChevronLeft />
            <strong>Order {ORDER_NO}</strong>
            <em className="app__live">Live</em>
          </div>
          <div className="app__body">
            <div className="pcard pcard--track">
              <motion.strong>{trackTitle}</motion.strong>
              <motion.span>{trackSub}</motion.span>
              <span className="ptrack">
                <motion.i style={{ width: queueWidth }} />
              </span>
            </div>
            <div className="pjourney">
              <div className="is-done">
                <i>
                  <FiCheck />
                </i>
                <span>Order placed</span>
              </div>
              <motion.div style={{ opacity: step2Op }}>
                <motion.i style={{ backgroundColor: step2Bg }}>
                  <FiCheck />
                </motion.i>
                <span>Printing</span>
              </motion.div>
              <motion.div style={{ opacity: step3Op }}>
                <motion.i style={{ backgroundColor: step3Bg }}>
                  <FiCheck />
                </motion.i>
                <span>Ready</span>
              </motion.div>
              <motion.div style={{ opacity: step4Op }}>
                <motion.i style={{ backgroundColor: step4Bg }}>
                  <FiCheck />
                </motion.i>
                <span>Collected</span>
              </motion.div>
            </div>
            <div className="pcard pcard--shop">
              <small>Collect at</small>
              <strong>{SHOP.name}</strong>
              <span>
                {SHOP.address}, {SHOP.distance}
              </span>
              <em>Show order {ORDER_NO} at the counter</em>
            </div>
            <div className="pscreen__cta pscreen__cta--ghost">
              <FiNavigation /> Get directions
            </div>
          </div>
        </motion.div>
      </PhoneFrame>
    </div>
  );
}
